import {
  useFormatToCurrencyCutoff,
  useFormatToDecimal,
  useTranslation,
} from '@dreamstack/i18n'
import { Marker } from '@dreamstack/icons/src/icon-components/Marker'
import { Share } from '@dreamstack/icons/src/icon-components/Share'
import { cn } from '@dreamstack/util'
import type { FullProject } from '@dreamstack/web/lib/prisma/projects'
import type { PropertyPreview } from '@dreamstack/web/lib/prisma/properties'
import CloseIcon from '@mui/icons-material/Close'
import first from 'lodash/first'
import minBy from 'lodash/minBy'
import some from 'lodash/some'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import { useEffect, useState } from 'react'
import { PropAndProjectContactForm } from '../contactForm/PropAndProjectContactForm'
import { ShareContactWrapper } from '../contactForm/ShareContactWrapper'
import { SimpleMuiButton } from '../layout/SimpleMuiButton'
import {
  Css_Text_20_30_To_16_24_tw,
  Css_Text_32_44_To_16_24_tw,
  HeadingH3,
  HeadingH4,
} from '../layout/Texts'
import { blurText } from '../styles/styles'
import { useWebShareAuto } from '../webshare/useWebShareAuto'
import { ProjectInformationList } from '../project/ProjectInformationList'
import { PropertyContactButton } from './PropertyContactButton'
import { scrollToMap } from './map/scrollToMap'

const containerClass =
  'col-span-12 px-px-24 pt-px-32 md:px-px-40 md:pt-px-48 ml:px-px-48 ml:pt-px-64 lg:col-span-8 lg:col-start-1'

const priceClass =
  'text-px-24 leading-px-36 md:text-px-40 md:leading-px-50 ml:text-px-48 ml:leading-px-50 font-semibold inline'
const priceTextClass =
  'text-px-16 leading-px-24 mr-px-8 md:text-px-24 md:leading-px-36 md:mr-px-12 ml:text-px-28 ml:leading-px-42 inline font-semibold'
const priceSubTextClass =
  'font-normal text-px-16 leading-px-24 pb-px-8 md:pb-px-16 ml:text-px-20 ml:leading-px-30'

export const ProjectInformation: FunctionComponent<
  React.PropsWithChildren<{
    properties?: PropertyPreview[]
    property?: PropertyPreview
    project?: FullProject
    statusLabel?: string
    isOnDarkBg?: boolean
  }>
> = ({ properties = [], property, project }) => {
  const formatCurrency = useFormatToCurrencyCutoff()
  const t = useTranslation()
  const formatDecimal = useFormatToDecimal()
  const { canShare, share } = useWebShareAuto()
  const [openContactForm, setOpenContactForm] = useState<boolean>(false)

  // fix begin
  // description https://github.com/vercel/next.js/discussions/17443
  const [mounted, setMounted] = useState(false)
  useEffect(() => {
    setMounted(true)
  }, [])
  // fix end

  // TODO: Refactor.
  // Filter out properties with price_per_sqm under 1500, requested by accentro
  let propertiesWithPriceOver1500 = properties.filter(
    (p) => p?.price_per_sqm && p?.price_per_sqm > 1500
  )

  // If there are no properties with price_per_sqm over 1500, use all properties to avoid mistakes like 0€ price
  if (propertiesWithPriceOver1500.length === 0) {
    propertiesWithPriceOver1500 = properties
  }
  const safeProperty =
    property ||
    (minBy(propertiesWithPriceOver1500, (p) => p?.price) ??
      first(propertiesWithPriceOver1500))

  const hasPrice = !(safeProperty?.price == null)

  const price = hasPrice ? formatCurrency(safeProperty.price!) : '???.???'

  const price_per_sqm =
    hasPrice && safeProperty.price_per_sqm
      ? formatDecimal(safeProperty.price_per_sqm)
      : '???'

  const projectHasGeoLocation = some(properties, (p) => !!p.geoLocation)

  const showGotoMapButton = !!property?.geoLocation || projectHasGeoLocation

  const logoUrl = project?.logo_url

  return (
    <>
      <div className={containerClass}>
        <div className="flex flex-col md:flex-row">
          {logoUrl && (
            <div className="h-px-160 w-px-274 mb-px-32 md:mr-px-48 self-center relative">
              <Image
                className="object-contain"
                layout="fill"
                src={logoUrl}
                alt="Project Logo"
              />
            </div>
          )}
          <div className="flex justify-between">
            <div className="flex flex-col flex-wrap">
              <HeadingH3>
                <div>
                  {safeProperty?.street + ' ' + safeProperty?.house_number}
                </div>
                <div>{safeProperty?.zip_code + ' ' + safeProperty?.city}</div>
              </HeadingH3>
              {showGotoMapButton && (
                <div className="text-accentroAqua-full font-semibold py-px-4 mt-px-16 md:mt-px-20 lg:mt-px-24 cursor-pointer">
                  <div className="flex justify-start items-center">
                    <Marker className="h-px-32 w-px-32" />
                    <span
                      className={cn('ml-px-12', Css_Text_20_30_To_16_24_tw)}
                      onClick={scrollToMap}
                      onKeyDown={scrollToMap}
                      role="button"
                      tabIndex={0}
                    >
                      {t('accentro:showOnMap')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <ProjectInformationList properties={properties} project={project} />
      </div>
      <div className="col-span-12 px-px-24 mt-px-40 md:mt-px-32 md:px-px-40 md:flex md:justify-between md:pb-px-64 lg:mt-px-80">
        <div className={cn('relative', !hasPrice && blurText)}>
          <h2 className={priceTextClass}>{t('accentro:startingAt')}</h2>
          <h2 className={priceClass}>
            {price}{' '}
            <span className={cn(`font-semibold`, Css_Text_32_44_To_16_24_tw)}>
              EUR
            </span>
          </h2>
          <h4 className={priceSubTextClass}>
            {price_per_sqm} / {t('accentro:squareMeter')}
          </h4>
          <h4 className={priceSubTextClass}>
            {t('accentro:freeOfCommission')}
          </h4>
        </div>
        <ShareContactWrapper canShare={mounted && canShare} isProperty={false}>
          {mounted && canShare && (
            <Share
              className="h-px-32 w-px-32 mt-px-24 cursor-pointer md:mt-0"
              onClick={() => share()}
            />
          )}
          {hasPrice && (
            <div className="md:mt-px-32">
              {!!openContactForm ? (
                <div className="flex flex-wrap my-px-32 md:my-0">
                  <SimpleMuiButton
                    variant="text"
                    className="focus:outline-none cursor-pointer"
                    onClick={() => {
                      setOpenContactForm(false)
                    }}
                  >
                    <HeadingH4 className="text-accentroWhite-full md:px-px-16">
                      {t('accentro:contactform.discardButton')}
                    </HeadingH4>
                    <CloseIcon className="text-accentroWhite-full" />
                  </SimpleMuiButton>
                </div>
              ) : (
                <div>
                  <PropertyContactButton
                    onClick={() => {
                      setOpenContactForm(true)
                    }}
                  />
                </div>
              )}
            </div>
          )}
        </ShareContactWrapper>
      </div>
      <div className={cn('col-span-12', !openContactForm && 'hidden')}>
        <PropAndProjectContactForm projectId={project?.id} isOnDarkBg={true} />
      </div>
    </>
  )
}
